import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, getAuthToken, getCoachResourceRevision, listCoachResources } from '@/lib/api';
import { resourceReadKey } from '@/lib/queryClient';
import type { FeedbackDraftScope, ResourceListQuery, ResourcePin } from '@/lib/types';
import { useFeedbackAuthoringBoundary } from './useFeedbackAuthoringScope';
import type { FeedbackResourceLinkOutcome, FeedbackResourceLinkTarget, FeedbackResourceLinkTargetRegistration,
  FeedbackExistingResourceLink } from '../resourceCreationContract';

type AdmissionOptions = {
  scope: FeedbackDraftScope; entryId: string; active: boolean; disabled: boolean;
  getPins: () => readonly ResourcePin[]; onChange: (pins: ResourcePin[]) => void;
  onTargetChange?: FeedbackResourceLinkTargetRegistration;
};
type AdmissionLifetime = {
  target: FeedbackResourceLinkTarget; controller: AbortController; mounted: boolean;
  generation: number; pending: number; tail: Promise<void>; reads: Set<AbortController>;
};
const retained = (reason: Extract<FeedbackResourceLinkOutcome, { status: 'retained' }>['reason']): FeedbackResourceLinkOutcome => ({ status: 'retained', reason });

/** Mounted admission only: no Resource command, receipt decoder or persistence.
 * Lives in ComposerForm, independent of the picker and its review submount. */
export function useFeedbackResourceAdmission(options: AdmissionOptions) {
  const boundary = useFeedbackAuthoringBoundary(options.scope);
  const latest = useRef(options); latest.current = options;
  const lifetime = useRef<AdmissionLifetime>();
  const [pending, setPending] = useState(false);
  const suspend = (life: AdmissionLifetime) => {
    life.generation++; life.pending = 0; life.tail = Promise.resolve();
    life.reads.forEach(read => read.abort()); life.reads.clear();
  };
  const departure = (life: AdmissionLifetime) => {
    const current = latest.current, captured = life.target.scope;
    if (!life.mounted || life.controller.signal.aborted || !boundary.current()
      || current.entryId !== life.target.entryId || current.scope.actorId !== captured.actorId
      || current.scope.clubId !== captured.clubId || current.scope.teamId !== captured.teamId
      || current.scope.seasonId !== captured.seasonId) {
      life.controller.abort(); return true;
    }
    return false;
  };
  const state = (life: AdmissionLifetime, generation = life.generation): FeedbackResourceLinkOutcome | null => {
    if (departure(life)) return retained('departed-target');
    if (generation !== life.generation || !latest.current.active || latest.current.disabled) return retained('inactive-target');
    return null;
  };
  useEffect(() => {
    const controller = new AbortController();
    const life: AdmissionLifetime = { controller, mounted: true, generation: 0, pending: 0,
      tail: Promise.resolve(), reads: new Set(), target: null! };
    const admitPin = (saved: FeedbackExistingResourceLink): Promise<FeedbackResourceLinkOutcome> => {
      const blocked = state(life); if (blocked) return Promise.resolve(blocked);
      const generation = life.generation, token = getAuthToken();
      // Capture original exact IDs/kind before any await; never resolve latest.
      const pin = { resource_id: saved.pin.resource_id, revision_id: saved.pin.revision_id }, kind = saved.kind;
      life.pending++; setPending(true);
      const work = life.tail.then(async (): Promise<FeedbackResourceLinkOutcome> => {
        const blocked = state(life, generation); if (blocked) return blocked;
        if (token !== getAuthToken()) return retained('inactive-target');
        const read = new AbortController(); life.reads.add(read);
        try {
          const result = await getCoachResourceRevision(pin, { scope: life.target.scope, signal: read.signal });
          const blocked = state(life, generation); if (blocked) return blocked;
          if (read.signal.aborted || token !== getAuthToken()) return retained('inactive-target');
          const value = result.resource;
          if (value.resource_id !== pin.resource_id || value.revision_id !== pin.revision_id
            || value.kind !== kind || value.availability !== 'available' || value.archived) return retained('unavailable-resource');
          // Receipt version is historical. Current mutable root version is not CAS here.
          const pins = latest.current.getPins(), existing = pins.find(value => value.resource_id === pin.resource_id);
          if (existing) return existing.revision_id === pin.revision_id ? { status: 'already-linked' } : retained('replacement-required');
          if (pins.length >= 10) return retained('resource-limit');
          latest.current.onChange([...pins, pin]);
          return { status: 'linked' };
        } catch (cause) {
          const blocked = state(life, generation); if (blocked) return blocked;
          if (read.signal.aborted || token !== getAuthToken()) return retained('inactive-target');
          const code = cause instanceof ApiError && typeof cause.detail === 'object' && cause.detail && 'code' in cause.detail ? cause.detail.code : null;
          if (cause instanceof ApiError && (cause.status === 401 || ['resource_auth_required', 'resource_access_required', 'resource_scope_invalid'].includes(String(code)))) {
            life.controller.abort(); return retained('departed-target');
          }
          return retained(cause instanceof ApiError && [403, 404].includes(cause.status) ? 'unavailable-resource' : 'link-failed');
        } finally { life.reads.delete(read); }
      });
      life.tail = work.then(() => undefined, () => undefined);
      return work.finally(() => {
        if (life.mounted && generation === life.generation) { life.pending--; setPending(life.pending > 0); }
      });
    };
    life.target = Object.freeze({ scope: Object.freeze({ ...latest.current.scope }), entryId: latest.current.entryId,
      signal: controller.signal, admitPin, admit: saved => admitPin({ kind: saved.kind, pin: saved.receipt }) });
    lifetime.current = life;
    return () => { life.mounted = false; controller.abort(); suspend(life); };
    // One target per mounted entry; current options are checked through latest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const target = lifetime.current!.target;
    options.onTargetChange?.(target);
    return () => { options.onTargetChange?.(null); };
  }, [options.onTargetChange]);
  useLayoutEffect(() => {
    const life = lifetime.current;
    if (life && (departure(life) || !options.active || options.disabled || !boundary.active)) {
      suspend(life); setPending(false);
    }
  }, [options.active, options.disabled, options.entryId, options.scope.actorId,
    options.scope.clubId, options.scope.teamId, options.scope.seasonId, boundary.active]);
  return { pending, isPending: () => (lifetime.current?.pending ?? 0) > 0,
    target: () => lifetime.current && !departure(lifetime.current) ? lifetime.current.target : null };
}

type Options = {
  scope: FeedbackDraftScope; pins: readonly ResourcePin[]; active: boolean; disabled: boolean;
  open: boolean; query: ResourceListQuery; onChange?: (pins: ResourcePin[]) => void;
};

/** Q01 reads only. All writes remain in the canonical Feedback command helpers. */
export function useFeedbackResources(options: Options) {
  const boundary = useFeedbackAuthoringBoundary(options.scope);
  const client = useQueryClient();
  const latest = useRef(options); latest.current = options;
  const alive = useRef(true), generation = useRef(0), adding = useRef(false);
  const [pending, setPending] = useState(false), [error, setError] = useState<string | null>(null);
  const denial = useRef(false), [denied, setDenied] = useState(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; generation.current++; }; }, []);
  useEffect(() => {
    if (!options.active || options.disabled || !options.open || !boundary.active) {
      generation.current++; adding.current = false; setPending(false);
    }
  }, [options.active, options.disabled, options.open, boundary.active]);
  const assertCurrent = (signal: AbortSignal) => {
    if (!boundary.current() || signal.aborted || denial.current) throw new DOMException('Resource context changed', 'AbortError');
  };
  async function read<T>(signal: AbortSignal, request: () => Promise<T>) {
    assertCurrent(signal);
    try { const result = await request(); assertCurrent(signal); return result; }
    catch (cause) {
      assertCurrent(signal);
      const code = cause instanceof ApiError && typeof cause.detail === 'object' && cause.detail && 'code' in cause.detail ? cause.detail.code : null;
      if (cause instanceof ApiError && (cause.status === 401 || ['resource_auth_required', 'resource_access_required', 'resource_scope_invalid'].includes(String(code)))) {
        denial.current = true; setDenied(true);
      }
      throw cause;
    }
  }
  const listOptions = (query: ResourceListQuery) => ({
    queryKey: resourceReadKey(options.scope, { projection: 'list', query }),
    queryFn: ({ signal }: { signal: AbortSignal }) => read(signal, () => listCoachResources(query, { scope: options.scope, signal })),
    enabled: boundary.active && !denied && options.active && options.open,
    staleTime: 0, retry: false, refetchOnWindowFocus: false,
  });
  // Q01's total is filtered. A separate unfiltered active query determines the
  // approved >5 toolbar rule without inventing an authorized_total field.
  const baseline = useQuery(listOptions({ archive: 'active', limit: 20 }));
  const list = useQuery(listOptions({ ...options.query, archive: 'active' }));
  const exactOptions = (pin: ResourcePin) => ({
    queryKey: resourceReadKey(options.scope, { projection: 'resource', resourceId: pin.resource_id, revisionId: pin.revision_id }),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const value = await read(signal, () => getCoachResourceRevision(pin, { scope: options.scope, signal }));
      if (value.resource.resource_id !== pin.resource_id || value.resource.revision_id !== pin.revision_id)
        throw new Error('Resource revision changed');
      return value;
    }, staleTime: 0, retry: false, refetchOnWindowFocus: false,
  });
  // Coming back to this tab rereads each pin, so Update version reflects a
  // version saved meanwhile, for example in the editor Open in editor opened.
  const selected = useQueries({ queries: options.pins.map(pin => ({ ...exactOptions(pin),
    enabled: boundary.active && !denied && options.active, refetchOnWindowFocus: true })) });
  const editable = () => alive.current && boundary.current() && !denial.current && latest.current.active && !latest.current.disabled;
  async function select(pin: ResourcePin) {
    if (!editable() || !latest.current.open || adding.current || !latest.current.onChange
      || latest.current.pins.length >= 10 || latest.current.pins.some(value => value.resource_id === pin.resource_id)) return;
    const request = ++generation.current, token = getAuthToken();
    adding.current = true; setPending(true); setError(null);
    const current = () => editable() && latest.current.open && request === generation.current && token === getAuthToken();
    try {
      // Capture the displayed exact revision, never resolve the mutable root.
      const result = await client.fetchQuery(exactOptions(pin));
      if (!current()) return;
      if (result.resource.availability !== 'available' || result.resource.archived) {
        setError('This Resource is no longer available to add. Your Feedback is unchanged.'); return;
      }
      if (latest.current.pins.length < 10 && !latest.current.pins.some(value => value.resource_id === pin.resource_id))
        latest.current.onChange?.([...latest.current.pins, { ...pin }]);
    } catch {
      if (current()) setError('Could not link this Resource. Your Feedback is unchanged. Try again.');
    } finally {
      if (request === generation.current && alive.current) { adding.current = false; setPending(false); }
    }
  }
  async function replaceVersion(original: ResourcePin, nextRevisionId: string) {
    const before = { ...original }, next = { resource_id: original.resource_id, revision_id: nextRevisionId };
    if (!editable() || adding.current || !latest.current.onChange || !nextRevisionId || nextRevisionId === before.revision_id
      || !latest.current.pins.some(pin => pin.resource_id === before.resource_id && pin.revision_id === before.revision_id)) return;
    const request = ++generation.current, token = getAuthToken();
    adding.current = true; setPending(true); setError(null);
    const current = () => editable() && request === generation.current && token === getAuthToken();
    try {
      // The explicit row action captures its displayed candidate. A later root
      // edit never silently advances it again; this is an exact revision read.
      const result = await client.fetchQuery(exactOptions(next));
      if (!current()) return;
      if (result.resource.availability !== 'available' || result.resource.archived) {
        setError('This version is no longer available to link. Your selected version is kept.'); return;
      }
      const pins = latest.current.pins;
      const index = pins.findIndex(pin => pin.resource_id === before.resource_id && pin.revision_id === before.revision_id);
      if (index < 0) { setError('Your selection changed while checking this version. Your current selection is kept.'); return; }
      // Replace in its current position, including at the ten-Resource limit.
      // Other pins and the surrounding form remain owned by the existing host.
      latest.current.onChange?.(pins.map((pin, position) => position === index ? next : pin));
    } catch {
      if (current()) setError('Could not update this Resource version. Your selected version is kept. Try again.');
    } finally {
      if (request === generation.current && alive.current) { adding.current = false; setPending(false); }
    }
  }
  const change = (pins: ResourcePin[]) => { if (editable() && !adding.current) latest.current.onChange?.(pins); };
  const blocked = denied || pending || selected.some(query => !query.isFetchedAfterMount || query.isFetching
    || !query.isSuccess || query.data.resource.availability !== 'available');
  return { authorized: boundary.active, denied, list, baseline, selected, pending, error, blocked, select, replaceVersion, change };
}
